import {
    getDefaultLanguage,
    getLanguageFallback,
    languageCode as languageCodeFunction,
} from './helpers'
import isIn from 'validator/lib/isIn'
import i18n from './i18n'
import store, { asyncLocalStorage } from '../admin/store'
import { SET_LOCALE_LANG } from '../admin/reducers/locale'
import { AVAILABLE_LANGUAGES } from '../consts'

/**
 * Maps available languages to url format
 *
 * @return {string[]}
 */
const mapAvailableLanguages = () => AVAILABLE_LANGUAGES.map(availableLanguage => `/${availableLanguage}/`)

/**
 * Get language from storage
 *
 * @return {Promise<unknown>}
 */
const getStorageLanguage = () => (
    asyncLocalStorage.getItem('language')
        .then(storageLanguage => {
            let resultLanguage = storageLanguage

            if (!storageLanguage || storageLanguage === '') {
                resultLanguage = getDefaultLanguage()
                asyncLocalStorage.setItem('language', getDefaultLanguage())
            }

            return new Promise(resolve => resolve(resultLanguage))
        })
)

/**
 * Set language. If not specified, redirect into right language
 */
export const setLanguage = () => {
    const pathname = window.location.pathname
    const languageCode = languageCodeFunction()
    const languagePathname = pathname.substring(0, 4)
    const availableLanguages = mapAvailableLanguages()

    getStorageLanguage()
        .then(defaultLanguage => {
            // If language is not defined or language code is not supported
            if (!isIn(languagePathname, availableLanguages)) {
                if (!isIn(`/${defaultLanguage}/`, availableLanguages)) {
                    defaultLanguage = getLanguageFallback(defaultLanguage)
                }

                // If some language code is not supported, redirect to right language
                if (pathname.substring(0, 1) === '/' && pathname.substring(3, 4) === '/') {
                    window.location.href = window.location.href.replace(languagePathname, `/${defaultLanguage}/`)
                    return
                }

                // Redirect to language with keeping original path
                window.location.href = window.location.href.replace(window.location.origin, `${window.location.origin}/${defaultLanguage}`)
                return
            }

            i18n.changeLanguage(languageCode)

            // Check if user change manually language in address bar
            if (languageCode !== defaultLanguage) {
                asyncLocalStorage.setItem('language', languageCode)
                updateApiLanguage(languageCode)
            }

            store.dispatch({
                type: SET_LOCALE_LANG,
                lang: languageCode,
            })
        })
}

/**
 * Update API language
 *
 * @param {string} language
 * @return {AxiosPromise<any>}
 */
export const updateApiLanguage = language => window.axios.patch(`${window.APP_CONFIG.REACT_APP_API_URL}api/users/me/update/language`, { language: language })

/**
 * Update API language without explicitly passed language - language will be taken from store
 *
 * @return {Promise<AxiosPromise<*>>}
 */
export const updateApiLanguageByStore = () => (
    getStorageLanguage()
        .then(storageLanguage => updateApiLanguage(storageLanguage))
)
